import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { supabase } from '../../../lib/supabase'
import { useTheme } from '../../../context/ThemeContext'
import NotificationBell from '../../../components/NotificationBell'

const CLUB_ID = 'a0000000-0000-0000-0000-000000000001'

const navItems = [
  { path: '/admin', label: 'Overview' },
  { path: '/admin/members', label: 'Members' },
  { path: '/admin/subscriptions', label: 'Subscriptions' },
  { path: '/admin/reports', label: 'Reports' },
  { path: '/admin/analytics', label: 'Analytics' },
  { path: '/admin/chapters', label: 'Chapters' },
  { path: '/admin/fixtures', label: 'Fixtures' },
  { path: '/admin/welfare', label: 'Welfare' },
  { path: '/admin/shop', label: 'Shop' },
  { path: '/admin/invite', label: 'Invite Coordinator' },
  { path: '/admin/scan', label: 'Scan Card' },
  { path: '/admin/settings', label: 'Settings' },
]

const SunIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="5"/>
    <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
)

const MoonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
  </svg>
)

const AdminFooter = ({ clubName }) => (
  <footer className="border-t border-black/10 dark:border-white/10 px-6 py-8 mt-auto">
    <div className="flex flex-col sm:flex-row justify-between gap-6">
      <div>
        <div className="flex items-center gap-2 mb-3">
          <img src="/Mufulira_Wanderers_logo.png" alt="Logo" className="w-6 h-6 object-contain" />
          <span className="font-black text-gray-900 dark:text-white tracking-widest uppercase text-xs">{clubName || 'MWFC'}</span>
        </div>
        <p className="text-gray-400 text-xs">Club Administration Portal</p>
      </div>
      <div className="flex gap-8">
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-3">Quick Links</div>
          <div className="flex flex-col gap-2">
            <Link to="/admin" className="text-gray-400 hover:text-gray-900 dark:hover:text-white text-xs transition-colors">Overview</Link>
            <Link to="/admin/members" className="text-gray-400 hover:text-gray-900 dark:hover:text-white text-xs transition-colors">Members</Link>
            <Link to="/admin/subscriptions" className="text-gray-400 hover:text-gray-900 dark:hover:text-white text-xs transition-colors">Subscriptions</Link>
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-3">Management</div>
          <div className="flex flex-col gap-2">
            <Link to="/admin/chapters" className="text-gray-400 hover:text-gray-900 dark:hover:text-white text-xs transition-colors">Chapters</Link>
            <Link to="/admin/fixtures" className="text-gray-400 hover:text-gray-900 dark:hover:text-white text-xs transition-colors">Fixtures</Link>
            <Link to="/admin/settings" className="text-gray-400 hover:text-gray-900 dark:hover:text-white text-xs transition-colors">Settings</Link>
          </div>
        </div>
      </div>
    </div>
    <div className="border-t border-black/10 dark:border-white/10 pt-6 mt-6">
      <p className="text-gray-400 text-xs uppercase tracking-widest">
        © {new Date().getFullYear()} MWFC
      </p>
    </div>
  </footer>
)

export default function AdminLayout({ children }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { isDark, toggleTheme } = useTheme()
  const [club, setClub] = useState(null)
  const [sidebarOpen, setSidebarOpen] = useState(false)

  useEffect(() => {
    const fetchClub = async () => {
      const { data } = await supabase
        .from('clubs').select('name, logo_url').eq('id', CLUB_ID).single()
      if (data) setClub(data)
    }
    fetchClub()
  }, [])

  const handleLogout = async () => {
    await supabase.auth.signOut()
    navigate('/staff/login')
  }

  const SidebarContent = () => (
    <>
      <div className="px-6 py-6 border-b border-black/10 dark:border-white/10">
        <Link to="/admin" className="flex items-center gap-3" onClick={() => setSidebarOpen(false)}>
          {club?.logo_url ? (
            <img src={club.logo_url} alt="Club logo" className="w-8 h-8 object-contain" />
          ) : (
            <img src="/Mufulira_Wanderers_logo.png" alt="Mufulira Wanderers" className="w-8 h-8 object-contain" />
          )}
          <div>
            <div className="font-black text-gray-900 dark:text-white tracking-widest uppercase text-sm">
              {club?.name || 'MWFC'}
            </div>
            <div className="text-gray-400 dark:text-gray-600 text-xs">Club Admin</div>
          </div>
        </Link>
      </div>

      <nav className="flex-1 px-3 py-4 overflow-y-auto">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center px-4 py-3 text-sm font-semibold uppercase tracking-widest transition-colors mb-1 border-l-2 ${
                isActive
                  ? 'text-green-600 border-green-500 bg-green-50 dark:bg-green-900/20'
                  : 'text-gray-400 border-transparent hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5'
              }`}
            >
              {item.label}
            </Link>
          )
        })}
      </nav>

      <div className="px-3 py-4 border-t border-black/10 dark:border-white/10 flex items-center justify-between">
        <button
          onClick={handleLogout}
          className="text-left px-4 py-3 text-sm font-semibold uppercase tracking-widest text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
        >
          Logout
        </button>
        <div className="flex items-center gap-2">
          <button onClick={toggleTheme} className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
            {isDark ? <SunIcon /> : <MoonIcon />}
          </button>
          <NotificationBell />
        </div>
      </div>
    </>
  )

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black text-gray-900 dark:text-white flex">

      {/* Mobile Top Bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white dark:bg-gray-950 border-b border-black/10 dark:border-white/10 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {club?.logo_url ? (
            <img src={club.logo_url} alt="Club logo" className="w-7 h-7 object-contain" />
          ) : (
            <img src="/Mufulira_Wanderers_logo.png" alt="Mufulira Wanderers" className="w-7 h-7 object-contain" />
          )}
          <span className="font-black text-gray-900 dark:text-white tracking-widest uppercase text-sm">
            {club?.name || 'Admin'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell />
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            {sidebarOpen ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Mobile Drawer */}
      <div className={`lg:hidden fixed top-0 left-0 h-full w-56 z-50 bg-white dark:bg-gray-950 border-r border-black/10 dark:border-white/10 flex flex-col transform transition-transform duration-200 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <SidebarContent />
      </div>

      {/* Desktop Sidebar — sticky not fixed so notification can overflow */}
      <div className="hidden lg:flex w-56 shrink-0 bg-white dark:bg-gray-950 border-r border-black/10 dark:border-white/10 flex-col sticky top-0 h-screen overflow-visible">
        <SidebarContent />
      </div>

      {/* Main Content + Footer */}
      <div className="flex-1 flex flex-col min-w-0 overflow-x-hidden mt-14 lg:mt-0 relative">
        {/* Watermark */}
        <div
          className="absolute inset-0 bg-center bg-no-repeat bg-contain opacity-[0.08] dark:opacity-[0.06] pointer-events-none"
          style={{ backgroundImage: "url('/Mufulira_Wanderers_logo.png')" }}
        />
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          {children}
        </main>
        <AdminFooter clubName={club?.name} />
      </div>
    </div>
  )
}